import React, { useState, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { Dropdown } from 'antd';
import Navigate from "../../components/Navigate";
import ResponsiveActionBar from "../../components/ResponsiveActionBar";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import ResponsiveModal from "../../components/ResponsiveModal";
import { exportToCSV, exportToPDF, notifyUser } from "../../util/util";
import { useMembers, useAllMembers, useMemberBeneficiaries, useSyncMembers, useGCNNominations } from "../../hooks/api";
import { useProfiles } from "../../hooks/api/useProfile";

const MembersTable = () => {
    const [exportLoading, setExportLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimer = useRef(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const [beneficiaryModalOpen, setBeneficiaryModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [nominationsModalOpen, setNominationsModalOpen] = useState(false);
    const [selectedMemberForNominations, setSelectedMemberForNominations] = useState(null);

    const serverPage = pagination.current - 1;

    const {
        data: pageData,
        isLoading,
        error,
        refetch
    } = useMembers(serverPage, pagination.pageSize, debouncedSearch);

    const content = pageData?.content || [];
    const total = pageData?.totalElements || 0;

    const {
        data: beneficiaries,
        isLoading: beneficiariesLoading
    } = useMemberBeneficiaries(
        selectedMember?.memberNumber || selectedMember?.member_no,
        selectedMember?.ssnitNumber || selectedMember?.ssnit_number,
        beneficiaryModalOpen && !!selectedMember
    );

    const { mutate: syncMembers, isPending: syncing } = useSyncMembers();
    const { data: profiles } = useProfiles();
    const activeProfile = profiles?.find((p) => p?.active) || profiles?.[0];
    const isGCB = activeProfile?.employerNumber === 'GCBT2/2024/0225';

    const {
        data: allMembers
    } = useAllMembers();

    const {
        data: nominations,
        isLoading: nominationsLoading
    } = useGCNNominations(
        selectedMemberForNominations?.ssnitNumber || selectedMemberForNominations?.ssnit_number,
        'ssnitNumber',
        nominationsModalOpen && !!selectedMemberForNominations
    );

    const handleViewBeneficiary = (record) => {
        setSelectedMember(record);
        setBeneficiaryModalOpen(true);
    };

    const closeBeneficiaryModal = () => {
        setBeneficiaryModalOpen(false);
        setSelectedMember(null);
    };

    const handleViewNominees = (record) => {
        setSelectedMemberForNominations(record);
        setNominationsModalOpen(true);
    };

    const closeNominationsModal = () => {
        setNominationsModalOpen(false);
        setSelectedMemberForNominations(null);
    };

    const handleSearch = useCallback((e) => {
        const value = e.target.value;
        setSearchText(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setDebouncedSearch(value);
            setPagination(prev => ({ ...prev, current: 1 }));
        }, 400);
    }, []);

    const handleTableChange = useCallback((newPagination) => {
        setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        });
    }, []);

    const columns = [
        { 
            title: 'Member Number', 
            dataIndex: 'memberNumber', 
            key: 'memberNumber', 
            width: 200,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Full Name', 
            dataIndex: 'fullName', 
            key: 'fullName', 
            width: 200,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'SSNIT Number', 
            dataIndex: 'ssnitNumber', 
            key: 'ssnitNumber', 
            width: 140,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
        },
        { 
            title: 'Ghana Card Number',
            dataIndex: 'ghanaCardNumber',
            key: 'ghanaCardNumber',
            width: 150,
            render: (text, record) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text || record?.tin || 'N/A'}</div>
        },
        { 
            title: 'Monthly Salary (GHS)', 
            dataIndex: 'monthlySalary', 
            key: 'monthlySalary', 
            width: 150,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? 'N/A'}</div>
        },
        { 
            title: 'Monthly Contribution (GHS)', 
            dataIndex: 'monthlyContribution', 
            key: 'monthlyContribution', 
            width: 180,
            render: text => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text ?? 'N/A'}</div>
        },
        ...(isGCB ? [{ 
            title: 'Actions', 
            key: 'actions', 
            fixed: 'right',
            width: 80,
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: "viewBeneficiary",
                                label: "View Beneficiary",
                                onClick: () => handleViewBeneficiary(record),
                            },
                            {
                                key: "viewDependents",
                                label: "View Dependents",
                                onClick: () => handleViewNominees(record),
                            },
                        ],
                    }}
                    overlayStyle={{ boxShadow: 'none' }}
                >
                    <span style={{ cursor: 'pointer', fontSize: 20 }} title="Actions">
                        <i className="bi bi-three-dots-vertical" />
                    </span>
                </Dropdown>
            )
        }] : [])
    ];

    const exportFunction = async (type) => {
        setExportLoading(true);
        try {
            const exportData = allMembers?.map(m => m.originalData) || content;
            if (type === 'pdf') {
                exportToPDF(columns, exportData, 'Members', true);
            } else {
                exportToCSV(exportData, 'Members');
            }
        } catch (error) {
            notifyUser('error', 'Export failed', 'Error');
        } finally {
            setExportLoading(false);
        }
    };

    if (error) {
        return (
            <div className="p-4">
                <div className="alert alert-danger">
                    Error loading members: {error.message}
                    <button className="btn btn-sm btn-primary ms-2" onClick={refetch}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <ResponsiveActionBar
                title="Members List"
                searchValue={searchText}
                onSearchChange={handleSearch}
                onExportCSV={() => exportFunction('csv')}
                onExportPDF={() => exportFunction('pdf')}
                exportLoading={exportLoading}
                extraActions={
                    <>
                        <button className="btn btn-white me-2" onClick={syncMembers} disabled={syncing}>
                            {syncing ? 'SYNCING...' : 'SYNC MEMBERS'} <i className="bi bi-arrow-clockwise" />
                        </button>
                        <Navigate path='/dashboard/members/new'>
                            <button className="btn btn-primary">
                                CREATE MEMBER <i className="bi bi-plus-circle" />
                            </button>
                        </Navigate>
                    </>
                }
            />
            
            <ResponsiveTableWrapper
                columns={columns}
                dataSource={content}
                loading={isLoading}
                rowKey={(record) => record.ssnitNumber || record.memberNumber}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} members`,
                }}
                onChange={handleTableChange}
            />
            <ResponsiveModal
                open={beneficiaryModalOpen}
                onCancel={closeBeneficiaryModal}
                title={selectedMember ? `Beneficiaries - ${selectedMember.fullName || selectedMember.FullName}` : 'Beneficiaries'}
                footer={null}
                width={900}
            >
                {beneficiariesLoading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : beneficiaries && beneficiaries.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Full Name</th>
                                    <th>Relationship</th>
                                    <th>Date of Birth</th>
                                    <th>Allocation (%)</th>
                                    <th>Pension Type</th>
                                    <th>Scheme Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {beneficiaries.map((b, i) => (
                                    <tr key={b.beneficiaryId || i}>
                                        <td>{b.fullName}</td>
                                        <td>{b.relationship}</td>
                                        <td>{b.birthDate ? dayjs(b.birthDate).format('MMM D, YYYY') : '-'}</td>
                                        <td>{b.percentageAllocation}%</td>
                                        <td>{b.pensionTypeName}</td>
                                        <td>{b.schemeName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-muted mb-0">No beneficiaries found for this member.</p>
                    </div>
                )}
            </ResponsiveModal>

            <ResponsiveModal
                open={nominationsModalOpen}
                onCancel={closeNominationsModal}
                title={selectedMemberForNominations ? `Dependents - ${selectedMemberForNominations.fullName || selectedMemberForNominations.FullName}` : 'Dependents'}
                footer={null}
                width={900}
            >
                {nominationsLoading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : nominations && nominations.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Full Name</th>
                                    <th>Relationship</th>
                                    <th>Date of Birth</th>
                                    <th>Phone</th>
                                    <th>ID Number</th>
                                    <th>Allocation (%)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {nominations.map((n) => (
                                    <tr key={n.id}>
                                        <td>{[n.firstname, n.middlename, n.lastname].filter(Boolean).join(' ') || 'N/A'}</td>
                                        <td>{n.relationship || '-'}</td>
                                        <td>{n.date_of_birth ? dayjs(n.date_of_birth).format('MMM D, YYYY') : '-'}</td>
                                        <td>{n.phone || '-'}</td>
                                        <td>{n.id_number || '-'}</td>
                                        <td>{n.percentage != null ? `${n.percentage}%` : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-muted mb-0">No dependents found for this member.</p>
                    </div>
                )}
            </ResponsiveModal>
        </div>
    );
};

export default MembersTable;
